import { Field, FieldProps, useFormikContext } from 'formik'
import React, { useEffect, useMemo, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'

import { Body1 } from '@ui/Typography'
import { Button } from '@ui/components/Button'
import { Link } from '@ui/components/Link'
import { WarningIcon } from '@ui/icons/Warning.icon'

import { validateIsTrue } from '@utils/formValidation'

import { LegalDocumentDto } from '@model/CompanyLegalDocumentDto'

import { PDFDocument } from '@global/PDFDocument/PDFDocument'

import { useScrollToTop } from '@hooks/useScrollToTop'

import { DocumentTemplate } from './DocumentTemplate'

import styles from './DocumentReadAndAgreeField.module.scss'

export interface AgreedLegalDocument {
  id: string
  openedOn: Date
  agreedOn: Date
}

interface DocumentFieldProps {
  documents: LegalDocumentDto[]
  renderItem?(props: DocumentFieldItem): React.ReactNode
}

export interface DocumentFieldItem {
  title: string
  isAccepted: boolean
  onClick(): void
  documents: LegalDocumentDto[]
  component: React.ReactNode
}

interface FormValues {
  documents: { [key: string]: boolean }
  agreedLegalDocuments: AgreedLegalDocument[]
  readDocuments: LegalDocumentDto[]
}

export const DocumentReadAndAgreeField: React.FC<DocumentFieldProps> = (props) => {
  const { documents = [], renderItem } = props

  const { setFieldValue } = useFormikContext<FormValues>()

  const handleDocumentModalOpen = (data: LegalDocumentDto) => {
    setFieldValue('readDocuments', [data])
  }

  return (
    <ReadAndAgreeField
      documents={documents}
      onDocumentModalOpen={handleDocumentModalOpen}
      renderItem={renderItem}
    />
  )
}

interface DocumentGroupFieldProps {
  groupReference: string
  documents: LegalDocumentDto[]
  renderItem?(props: DocumentFieldItem): React.ReactNode
}

export const DocumentReadAndAgreeGroupField: React.FC<DocumentGroupFieldProps> = (props) => {
  const { groupReference, documents, renderItem } = props

  const { setFieldValue, values } = useFormikContext<FormValues>()
  const { t } = useTranslation()

  const handleDocumentModalOpen = () => {
    setFieldValue('readDocuments', documents)
  }

  const allDocumentsAccepted = documents.every((doc) => values.documents?.[doc.id] === true)

  useEffect(() => {
    if (allDocumentsAccepted && groupReference && values.documents?.[groupReference] !== true) {
      setFieldValue(`documents.${groupReference}`, true)
    }
  }, [allDocumentsAccepted, groupReference, values.documents, setFieldValue])

  const titles = documents.map((doc) => doc.title).join(', ')

  const defaultComponent = (
    <ReadAndAgree
      title={titles}
      isAccepted={allDocumentsAccepted}
      onClick={handleDocumentModalOpen}
    />
  )

  return (
    <Field name={`documents.${groupReference}`} validate={validateIsTrue(t('Validation.Required'))}>
      {() => {
        return (
          <React.Fragment>
            {renderItem
              ? renderItem({
                  title: titles,
                  isAccepted: allDocumentsAccepted,
                  onClick: handleDocumentModalOpen,
                  documents,
                  component: defaultComponent,
                })
              : defaultComponent}
          </React.Fragment>
        )
      }}
    </Field>
  )
}

interface ReadAndAgreeFieldProps extends DocumentFieldProps {
  onDocumentModalOpen(document: LegalDocumentDto): void
  renderItem?(props: DocumentFieldItem): React.ReactNode
}

const ReadAndAgreeField: React.FC<ReadAndAgreeFieldProps> = (props) => {
  const { documents = [], onDocumentModalOpen, renderItem } = props
  const [document] = documents

  const { t } = useTranslation()

  return (
    <Field name={`documents.${document.id}`} validate={validateIsTrue(t('Validation.Required'))}>
      {(props: FieldProps<boolean>) => {
        const defaultComponent = (
          <ReadAndAgree
            title={document.title || ''}
            isAccepted={props.field.value}
            onClick={() => onDocumentModalOpen(document)}
          />
        )

        return (
          <React.Fragment>
            {renderItem
              ? renderItem({
                  title: document.title || '',
                  isAccepted: props.field.value,
                  onClick: () => onDocumentModalOpen(document),
                  documents,
                  component: defaultComponent,
                })
              : defaultComponent}
          </React.Fragment>
        )
      }}
    </Field>
  )
}

interface ReadAndAgreeProps {
  title: string
  isAccepted: boolean
  onClick(): void
}

export const ReadAndAgree: React.FC<ReadAndAgreeProps> = (props) => {
  const { title, isAccepted, onClick } = props

  const { t } = useTranslation()

  return (
    <div className='is-flex is-align-items-center'>
      {isAccepted ? (
        <div className='is-flex'>
          <span className='is-flex is-align-items-center pr-1'>
            <Body1>{title}</Body1>
          </span>
          <Body1>
            <Trans i18nKey='Sign up.has been accepted'>
              has been <span className='has-text-success'>accepted</span>
            </Trans>
          </Body1>
        </div>
      ) : (
        <div className='is-flex'>
          <span className='pr-1'>
            <Link onClick={onClick}>
              <Body1>{t('Sign up.Read & Accept')}</Body1>
            </Link>
          </span>
          <span className='is-flex is-align-items-center'>
            <Body1>{title}</Body1>
          </span>
        </div>
      )}
    </div>
  )
}

export const ReadDocumentPage: React.FC<{
  goBackFromDoc?: () => Promise<void>
}> = ({ goBackFromDoc }) => {
  const { t } = useTranslation()
  useScrollToTop()

  const { values, setValues, setFieldValue } = useFormikContext<FormValues>()

  const [isLoading, setLoading] = useState(true)
  const [scrolledToBottom, setScrolledToBottom] = useState(false)
  const readDocuments = values?.readDocuments

  const documents = readDocuments && readDocuments.length > 0 ? readDocuments : []

  const [currentDocumentIndex, setCurrentDocumentIndex] = useState(0)
  const [scrolledDocuments, setScrolledDocuments] = useState<Set<string>>(new Set())

  const currentDocument = documents[currentDocumentIndex]
  const isLastDocument = currentDocumentIndex === documents.length - 1
  const isMultiDocument = documents.length > 1

  useEffect(() => {
    if (scrolledToBottom && currentDocument) {
      setScrolledDocuments((prev) => {
        const newSet = new Set(prev)
        newSet.add(currentDocument.id)
        return newSet
      })
    }
  }, [scrolledToBottom, currentDocument?.id])

  useEffect(() => {
    if (currentDocument) {
      setScrolledToBottom(false)
      setLoading(true)
    }
  }, [currentDocumentIndex, currentDocument?.id])

  const handleNextDocument = () => {
    if (currentDocumentIndex < documents.length - 1 && currentDocument) {
      if (scrolledToBottom) {
        setScrolledDocuments((prev) => {
          const newSet = new Set(prev)
          newSet.add(currentDocument.id)
          return newSet
        })
      }
      setCurrentDocumentIndex(currentDocumentIndex + 1)
    }
  }

  const handleAccept = () => {
    if (isMultiDocument) {
      const updatedDocuments = { ...values.documents }
      const updatedAgreedLegalDocuments = [...values.agreedLegalDocuments]

      documents.forEach((doc) => {
        updatedDocuments[doc.id] = true
        const existingIndex = updatedAgreedLegalDocuments.findIndex((x) => x.id === doc.id)
        const agreedDoc =
          existingIndex >= 0
            ? updatedAgreedLegalDocuments[existingIndex]
            : createAgreedLegalReadAndAgreeDocument(doc)

        if (existingIndex >= 0) {
          updatedAgreedLegalDocuments[existingIndex] = {
            ...agreedDoc,
            agreedOn: new Date(),
          }
        } else {
          updatedAgreedLegalDocuments.push(agreedDoc)
        }
      })

      const groupReference = documents[0]?.groupReference
      if (groupReference) {
        updatedDocuments[groupReference] = true
      }

      setValues({
        ...values,
        documents: updatedDocuments,
        agreedLegalDocuments: updatedAgreedLegalDocuments.filter(
          filterAgreedLegalDocumentsDuplicates
        ),
      })
      handleDocumentModalClose()
    } else if (currentDocument?.code) {
      const [agreedLegalDocument] = values.agreedLegalDocuments.filter(
        (x) => x.id === currentDocument.id
      )

      const groupReference = currentDocument.groupReference || undefined
      const updatedDocuments = {
        ...values.documents,
        [currentDocument.id]: true,
      }

      if (groupReference) {
        updatedDocuments[groupReference] = true
      }

      setValues({
        ...values,
        documents: updatedDocuments,
        agreedLegalDocuments: values.agreedLegalDocuments
          .filter(filterAgreedLegalDocumentsByDocument(currentDocument))
          .concat(createAgreedLegalReadAndAgreeDocument(currentDocument, agreedLegalDocument))
          .filter(filterAgreedLegalDocumentsDuplicates),
      })
      handleDocumentModalClose()
    }
  }

  const handleDocumentModalClose = () => {
    if (goBackFromDoc) {
      goBackFromDoc()
    } else {
      setFieldValue('readDocuments', undefined)
    }
  }

  const handlePdfDocumentLoadSuccess = () => {
    setTimeout(() => {
      setLoading(false)
    }, 3000)
  }

  const docNote = useMemo(() => {
    const baseText = t('Validation.Please read and accept the') + ' '

    if (!currentDocument) {
      return baseText
    }

    return baseText + (currentDocument.title || '')
  }, [currentDocument?.title, t])

  const allDocumentsScrolled = isMultiDocument
    ? documents.every((doc) => {
        if (doc.id === currentDocument?.id) {
          return scrolledToBottom
        }
        return scrolledDocuments.has(doc.id)
      })
    : scrolledToBottom

  if (currentDocument?.code) {
    return (
      <DocumentTemplate title={currentDocument.title || ''} goBack={handleDocumentModalClose}>
        <div className={styles.documentNote}>
          <WarningIcon color='text-warning-high-em' />
          <span>{docNote}</span>
        </div>
        <PDFDocument
          file={currentDocument.url}
          setScrolledToBottom={setScrolledToBottom}
          onLoadSuccess={handlePdfDocumentLoadSuccess}
        />
        <div className={styles.agreeButtons}>
          <Button.Secondary size='lg' onClick={handleDocumentModalClose}>
            {t('Cancel')}
          </Button.Secondary>
          {isMultiDocument && !isLastDocument && (
            <Button.Primary
              size='lg'
              disabled={!scrolledToBottom || isLoading}
              onClick={handleNextDocument}
            >
              {t('Next')}
            </Button.Primary>
          )}
          {(isLastDocument || !isMultiDocument) && (
            <Button.Primary
              size='lg'
              disabled={!scrolledToBottom || !allDocumentsScrolled || isLoading}
              onClick={handleAccept}
            >
              {t('Accept')}
            </Button.Primary>
          )}
        </div>
      </DocumentTemplate>
    )
  }

  return null
}

const filterAgreedLegalDocumentsByDocument =
  (document: LegalDocumentDto) => (x: AgreedLegalDocument) => {
    return x.id !== document.id
  }

const createAgreedLegalReadAndAgreeDocument = (
  document: LegalDocumentDto,
  agreedLegalDocument?: AgreedLegalDocument
) => {
  return {
    id: document.id,
    openedOn: agreedLegalDocument?.openedOn || new Date(),
    agreedOn: new Date(),
  }
}

const filterAgreedLegalDocumentsDuplicates = (
  value: AgreedLegalDocument,
  index: number,
  self: AgreedLegalDocument[]
) => {
  return index === self.findIndex((t) => t.id === value.id && t.id === value.id)
}

export const renderCommaSeparated = (
  items: React.ReactNode[],
  separator = ', '
): React.ReactNode[] => {
  if (items.length === 0) {
    return []
  }

  return items.reduce<React.ReactNode[]>((prev, curr, i) => {
    if (i === 0) {
      return [curr]
    } else {
      return [...prev, separator, curr]
    }
  }, [])
}
